import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ResourceLoaderService {
  private readonly scriptLoads = new Map<string, Promise<void>>();

  /**
   * Lazily injects a script tag for `src`, deduplicating concurrent and
   * repeated requests so callers can safely invoke `load` on every use.
   */
  load(src: string): Promise<void> {
    const existing = this.scriptLoads.get(src);
    if (existing) {
      return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
      if (this.hasScript(src)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = src;
      script.onload = () => resolve();
      script.onerror = (error) => {
        script.remove();
        this.scriptLoads.delete(src);
        reject(error);
      };
      document.body.appendChild(script);
    });

    this.scriptLoads.set(src, promise);
    return promise;
  }

  private hasScript(src: string): boolean {
    return Array.from(document.getElementsByTagName('script')).some((el) => {
      const attr = el.getAttribute('src');
      return attr === src || el.src.endsWith(`/${src}`) || el.src.endsWith(src);
    });
  }

  imageBlobLoader(imageUrl: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', imageUrl, true);
      xhr.responseType = 'arraybuffer';
      xhr.timeout = 10000;

      xhr.onload = (e) => {
        const arrayBufferView = new Uint8Array(xhr.response);
        const blob = new Blob([arrayBufferView], {
          type: 'image/jpeg',
        });
        const url = URL.createObjectURL(blob);
        const status = xhr.status;
        const resp = {
          status: status,
          imageUrl: status === 200 ? url : null,
          lastModified:
            status === 200
              ? new Date(xhr.getResponseHeader('last-modified'))
              : null,
        };

        resolve(resp);
      };

      xhr.onerror = xhr.ontimeout = (err) => {
        reject({
          status: xhr.status,
          err: err,
        });
      };

      xhr.send();
    });
  }
}
